import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    IconButton,
    Tooltip
} from "@mui/material";
import {
    Star as StarIcon,
    DeleteOutline as DeleteOutlineIcon,
    StarBorder as StarBorderIcon
} from "@mui/icons-material";



function JudgesChoiceDialog({ open, onClose, loading, judgeChoices = [], judgesColIndexes }) {
    const { entryIdx, titleIdx, levelIdx, styleIdx, sizeIdx, schoolIdx,
        artisticIdx, technicalIdx, notesIdx, nomIdx, favIdx } = judgesColIndexes;

    const [localChoices, setLocalChoices] = useState([]);

    useEffect(() => {
        if (!open) return; // Only sync favourites when dialog is opened
        setLocalChoices((judgeChoices || []).map(row => [...row]));
    }, [open, judgeChoices]);

    function handleRemove(entry) {
        setLocalChoices(prev =>
            prev.map(row => {
                if (String(row[entryIdx]) !== String(entry[entryIdx]))
                    return row;
                const next = [...row];
                next[nomIdx] = next[nomIdx] === "true" ? "" : "true";
                return next;
            })
        );
    }

    function handleFavourite(entry) {
        setLocalChoices(prev =>
            prev.map(row => {
                if (String(row[entryIdx]) !== String(entry[entryIdx]))
                    return row;
                const next = [...row];
                next[favIdx] = next[favIdx] === "true" ? "" : "true";
                return next;
            })
        );
    }

    //user closes without saving — discard changes
    const handleDiscard = () => {
        const hasChanges = JSON.stringify(localChoices) !== JSON.stringify(judgeChoices);
        if (hasChanges) {
            const confirmed = window.confirm("You have unsaved changes. Close without saving?");
            if (!confirmed) return;
        }
        onClose?.();
    };

    const handleSave = () => {
        // Sync any changes back to the main judgeChoices data before closing
        // This is where you'd also want to persist changes to the backend if needed
        onClose?.(localChoices);
    }
    return (
        <Dialog open={open} onClose={handleDiscard} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: "1.25rem" }}>
                <StarIcon color="primary" /> Judge's Choice Nominations</DialogTitle>
            <DialogContent dividers>
                {loading ? (
                    <div style={{ display: "flex", justifyContent: "center", padding: "2rem" }}>
                        <CircularProgress />
                    </div>
                ) : (
                    <TableContainer component={Paper} elevation={0} variant="outlined">
                        <Table size="small"
                            sx={{
                                "& .MuiTableCell-root": {
                                    py: 0.2,   // vertical padding
                                    px: 1,     // horizontal padding
                                    whiteSpace: "nowrap",
                                },
                                tableLayout: "auto",
                            }} >
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontSize: "0.8rem", fontWeight: 600 }}>Fav</TableCell>
                                    <TableCell sx={{ fontSize: "0.8rem", fontWeight: 600 }}>Entry</TableCell>
                                    <TableCell sx={{ fontSize: "0.8rem", fontWeight: 600 }}>Title</TableCell>
                                    <TableCell sx={{ fontSize: "0.8rem", fontWeight: 600 }}>Level</TableCell>
                                    <TableCell sx={{ fontSize: "0.8rem", fontWeight: 600 }}>Style</TableCell>
                                    <TableCell sx={{ fontSize: "0.8rem", fontWeight: 600 }}>Size</TableCell>
                                    <TableCell sx={{ fontSize: "0.8rem", fontWeight: 600 }}>School</TableCell>
                                    <TableCell sx={{ fontSize: "0.8rem", fontWeight: 600 }}>Remove</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {localChoices.filter(entry => entry[nomIdx] === 'true')
                                    .map((entry, index) => (
                                        <Tooltip
                                            key={index}
                                            enterDelay={1000}
                                            title={
                                                <div style={{ fontSize: "0.8rem", lineHeight: 1.8 }}>
                                                    <div><strong>Artistic:</strong> {entry[artisticIdx] || "—"}</div>
                                                    <div><strong>Technical:</strong> {entry[technicalIdx] || "—"}</div>
                                                    <div><strong>Notes:</strong> {entry[notesIdx] || "—"}</div>
                                                </div>
                                            }
                                            placement="bottom"
                                            arrow
                                        >
                                            <TableRow key={index} hover>
                                                <TableCell>
                                                    <IconButton onClick={() => handleFavourite(entry)}
                                                        size="large"
                                                        title="Favourite"
                                                        sx={{ color: entry[favIdx] === 'true' ? '#EF9F27' : "inherit", padding: 0 }}
                                                    >{entry[favIdx] === 'true' ?
                                                        <StarIcon sx={{ fontSize: "1.25rem" }} />
                                                        :
                                                        <StarBorderIcon sx={{ fontSize: "1.25rem" }} />
                                                        }
                                                    </IconButton></TableCell>
                                                <TableCell sx={{ fontSize: "0.8rem" }}>{entry[entryIdx]}</TableCell>
                                                <TableCell sx={{ fontSize: "0.8rem" }}>{entry[titleIdx]}</TableCell>
                                                <TableCell sx={{ fontSize: "0.8rem" }}>{entry[levelIdx].slice(0, 3).toLowerCase() === 'nov' ? entry[levelIdx].slice(0, 3) : `${entry[levelIdx].slice(0, 3)}${entry[levelIdx].slice(-2)}`}</TableCell>
                                                <TableCell sx={{ fontSize: "0.8rem" }}>{entry[styleIdx]}</TableCell>
                                                <TableCell sx={{ fontSize: "0.8rem" }}>{entry[sizeIdx]}</TableCell>
                                                <TableCell sx={{ fontSize: "0.8rem" }}>{entry[schoolIdx]}</TableCell>
                                                <TableCell>
                                                    <IconButton onClick={() => handleRemove(entry)}
                                                        size="large"
                                                        title="Remove"
                                                        sx={{ padding: 0, "&:hover .MuiSvgIcon-root": { color: "error.main" }, }}
                                                    >
                                                        <DeleteOutlineIcon sx={{ fontSize: "1.25rem" }} />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        </Tooltip>
                                    ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={() => { handleSave(); }}>Save</Button>
                <Button onClick={() => { handleDiscard(); }}>Close</Button>
            </DialogActions>

        </Dialog>
    );

}

export default JudgesChoiceDialog;